import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Coffee, PartyPopper, Share2, Home, Bell, BellRing } from "lucide-react";
import PhoneShell from "../components/PhoneShell";
import LoyaltyDots from "../components/LoyaltyDots";
import { api } from "../lib/api";
import { clearCart, getPhone } from "../lib/session";
import { customLabel } from "../lib/session";
import { pushSupported, pushPermission, enablePushForPhone } from "../lib/push";
import { toast } from "sonner";

const Confirmation = () => {
    const { orderId, slug } = useParams();
    const ctx = useOutletContext() || {};
    const cafeName = ctx.tenant?.name || "Coffeein7";
    const nav = useNavigate();
    const [order, setOrder] = useState(null);
    const [err, setErr] = useState(null);
    const [pushState, setPushState] = useState(() => (pushSupported() ? pushPermission() : "unsupported"));
    const [enablingPush, setEnablingPush] = useState(false);

    const enablePush = async () => {
        setEnablingPush(true);
        try {
            await enablePushForPhone(getPhone());
            setPushState("granted");
            toast.success("You'll get a ping when your coffee is ready ☕");
        } catch (e) {
            toast.error(e?.message || "Couldn't enable notifications");
            setPushState(pushPermission());
        } finally {
            setEnablingPush(false);
        }
    };

    useEffect(() => {
        clearCart();
        let cancelled = false;
        const load = () => {
            api.get(`/orders/${orderId}`)
                .then((r) => !cancelled && setOrder(r.data))
                .catch(() => !cancelled && setErr("Order not found"));
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [orderId]);

    if (err)
        return (
            <PhoneShell>
                <div className="p-8 text-center">
                    <div className="text-[#8C7A72]">{err}</div>
                    <button
                        onClick={() => nav(`/${slug}/menu`)}
                        className="mt-4 px-6 py-3 rounded-full bg-[#FF4F18] text-white font-display font-bold"
                    >
                        Back to menu
                    </button>
                </div>
            </PhoneShell>
        );

    if (!order)
        return (
            <PhoneShell>
                <div className="p-8 text-center text-[#8C7A72]">Loading your order…</div>
            </PhoneShell>
        );

    const readyTime = new Date(order.pickup_ready_at);
    const timeStr = readyTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const stamps = order.loyalty_after?.stamps ?? 0;
    const unlocked = order.loyalty_unlocked_now;

    const qrData = `COFFEEIN7|${order.order_number}|${order.phone_last4}`;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
        qrData,
    )}&bgcolor=F9F6F0&color=2C1A12&margin=0`;

    return (
        <PhoneShell>
            <div className="px-5 pt-8 pb-16 min-h-screen">
                <motion.div
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    className="mx-auto h-20 w-20 rounded-full bg-[#1B995E] flex items-center justify-center shadow-[0_16px_40px_rgba(27,153,94,0.32)]"
                >
                    <CheckCircle2 className="h-10 w-10 text-white" strokeWidth={2.5} />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-center mt-6"
                >
                    <div className="text-xs uppercase tracking-[0.3em] text-[#8C7A72] font-semibold">
                        Order confirmed
                    </div>
                    <div
                        className="font-display font-black text-4xl mt-1"
                        data-testid="order-number"
                    >
                        {order.order_number}
                    </div>
                    {order.customer_name && (
                        <div
                            className="mt-2 inline-flex items-center gap-2 bg-[#FF4F18] text-white rounded-full px-3 py-1"
                            data-testid="order-name"
                        >
                            <span className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">
                                for
                            </span>
                            <span className="font-display font-bold text-sm">
                                {order.customer_name}
                            </span>
                        </div>
                    )}
                    <div className="mt-3 inline-flex items-center gap-2 bg-white border border-[#E8DCCB] rounded-full px-4 py-2">
                        <Coffee className="h-4 w-4 text-[#FF4F18]" strokeWidth={2.5} />
                        <span className="font-display font-bold text-[#2C1A12]">
                            Ready by {timeStr}
                        </span>
                    </div>
                    {order.payment_method === "counter" && (
                        <div className="mt-2 text-xs text-[#8C7A72]">Pay at the counter on pickup</div>
                    )}
                </motion.div>

                {/* QR */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mt-8 mx-auto w-fit bg-white rounded-3xl p-4 border border-[#E8DCCB]"
                >
                    <img
                        src={qrUrl}
                        alt="Pickup QR"
                        className="h-40 w-40"
                        data-testid="pickup-qr"
                    />
                    <div className="text-center text-[10px] uppercase tracking-[0.25em] text-[#8C7A72] mt-2">
                        Show at counter
                    </div>
                </motion.div>

                {/* Push notification enable — contextual, only if supported and not granted */}
                {pushState === "default" && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25 }}
                        className="mt-6 rounded-3xl bg-[#2C1A12] text-white p-5"
                        data-testid="push-prompt-card"
                    >
                        <div className="flex items-start gap-4">
                            <div className="h-11 w-11 rounded-2xl bg-[#FF4F18]/20 flex items-center justify-center shrink-0">
                                <BellRing className="h-5 w-5 text-[#FF4F18]" strokeWidth={2.5} />
                            </div>
                            <div className="flex-1">
                                <div className="font-display font-bold text-base leading-tight">
                                    Get a ping when it's ready
                                </div>
                                <div className="text-white/70 text-xs mt-1">
                                    We'll notify you the moment the barista marks your coffee ready. No spam.
                                </div>
                                <button
                                    data-testid="enable-push-btn"
                                    onClick={enablePush}
                                    disabled={enablingPush}
                                    className="mt-3 rounded-full bg-[#FF4F18] text-white text-xs font-display font-bold px-4 py-2 active:scale-95 disabled:opacity-50"
                                >
                                    {enablingPush ? "Enabling…" : "Turn on alerts"}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
                {pushState === "granted" && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mt-6 rounded-2xl bg-[#1B995E]/10 border border-[#1B995E]/30 p-3 flex items-center gap-2"
                        data-testid="push-on-badge"
                    >
                        <Bell className="h-4 w-4 text-[#1B995E]" strokeWidth={2.5} />
                        <span className="text-xs text-[#1B995E] font-bold">
                            Alerts on · we'll ping you when ready
                        </span>
                    </motion.div>
                )}

                {/* Loyalty */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-8 rounded-3xl bg-white border border-[#E8DCCB] p-5"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
                                Loyalty
                            </div>
                            <div
                                className="font-display font-black text-2xl mt-1"
                                data-testid="loyalty-count"
                            >
                                {stamps}/10
                            </div>
                        </div>
                        <LoyaltyDots stamps={stamps} size="md" />
                    </div>
                    {unlocked && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="mt-4 rounded-2xl bg-[#FF4F18] text-white p-4 flex items-center gap-3"
                            data-testid="free-unlocked-banner"
                        >
                            <PartyPopper className="h-6 w-6 shrink-0" strokeWidth={2.5} />
                            <div>
                                <div className="font-display font-black">Free coffee unlocked!</div>
                                <div className="text-sm text-white/80">
                                    Show this screen next visit
                                </div>
                            </div>
                        </motion.div>
                    )}
                    {order.free_coffee_applied && (
                        <div className="mt-3 text-sm text-[#1B995E] font-semibold">
                            ✓ You redeemed a free coffee on this order
                        </div>
                    )}
                </motion.div>

                {/* Items summary */}
                <div className="mt-6 rounded-3xl bg-white border border-[#E8DCCB] p-5">
                    <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold mb-3">
                        Order details
                    </div>
                    <div className="flex flex-col gap-2">
                        {order.items.map((i, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                                <span>
                                    {i.qty} × {i.name}
                                    {i.size ? ` (${i.size === "L" ? "Large" : "Small"})` : ""}
                                    {i.customizations && customLabel(i.customizations) && (
                                        <span className="block text-[11px] text-[#FF4F18] font-semibold">
                                            {customLabel(i.customizations)}
                                        </span>
                                    )}
                                </span>
                                <span className="font-semibold">
                                    ${(i.qty * i.price).toFixed(2)}
                                </span>
                            </div>
                        ))}
                        <div className="border-t border-[#E8DCCB] my-2" />
                        <div className="flex justify-between text-sm">
                            <span className="text-[#8C7A72]">Total paid</span>
                            <span className="font-display font-black text-lg">
                                ${order.total.toFixed(2)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Share */}
                <div className="mt-6 grid grid-cols-2 gap-3">
                    <button
                        data-testid="share-btn"
                        onClick={() => {
                            const refCode = order.phone_last4;
                            const shareUrl = `${window.location.origin}/${slug}/?ref=${refCode}`;
                            const shareText = `I skip the coffee queue at ${cafeName} with Coffeein7 ☕ — order ahead, get a stamp on us. ${shareUrl}`;
                            if (navigator.share) {
                                navigator.share({
                                    title: `${cafeName} — Bring a friend`,
                                    text: shareText,
                                    url: shareUrl,
                                });
                            } else {
                                navigator.clipboard.writeText(shareText);
                            }
                        }}
                        className="py-4 rounded-full bg-white border border-[#E8DCCB] font-display font-bold flex items-center justify-center gap-2 active:scale-95"
                    >
                        <Share2 className="h-4 w-4" strokeWidth={2.5} />
                        Bring a friend
                    </button>
                    <button
                        data-testid="home-btn"
                        onClick={() => nav(`/${slug}/menu`)}
                        className="py-4 rounded-full bg-[#2C1A12] text-white font-display font-bold flex items-center justify-center gap-2 active:scale-95"
                    >
                        <Home className="h-4 w-4" strokeWidth={2.5} />
                        Order more
                    </button>
                </div>

                <div className="mt-6 text-center text-xs text-[#8C7A72]">
                    ···{order.phone_last4} · {getPhone() ? "Your account" : ""}
                </div>
            </div>
        </PhoneShell>
    );
};

export default Confirmation;
